(function () {
  const NUDGE_CLASS = 'product-marquee__track--nudging';
  const RESUME_DELAY = 3000;
  const DRAG_THRESHOLD = 6;

  class ProductMarquee extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.product-marquee__track');
      this.group = this.track ? this.track.querySelector('.product-marquee__group') : null;
      this.speedSeconds = parseFloat(this.dataset.speed) || 40;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.offset = 0;
      this.groupWidth = 0;
      this.paused = false;
      this.hovering = false;
      this.dragging = false;
      this.dragAxis = null;
      this.resumeTimeout = null;
      this.lastTime = null;
    }

    connectedCallback() {
      if (!this.track || !this.group) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const container = this.closest('.product-marquee__container');
      this.prevButton = container?.querySelector('[data-marquee-prev]');
      this.nextButton = container?.querySelector('[data-marquee-next]');
      this.prevButton?.addEventListener('click', () => this.nudge(-1));
      this.nextButton?.addEventListener('click', () => this.nudge(1));

      this.track.addEventListener('transitionend', (event) => {
        if (event.propertyName !== 'transform') return;
        // The transition target is a raw, never-wrapped offset so it always
        // animates forward/backward into a real clone. Once it lands, fold it
        // back into a small equivalent value (invisible: the track is
        // periodic, so this remap never changes what's on screen).
        this.settle();
        this.render();
      });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => (this.hovering = true));
        this.addEventListener('mouseleave', () => (this.hovering = false));
      }

      // Drag-to-scroll: covers touch swipes on mobile and click-drag with a mouse.
      this.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.addEventListener('pointercancel', this.onPointerUp.bind(this));

      // Trackpad two-finger left/right swipe (sends wheel events with deltaX).
      this.addEventListener('wheel', this.onWheel.bind(this), { passive: false });

      // Without this, a mouse-drag on a card image starts the browser's native
      // "drag the image" ghost instead of our own drag-to-scroll.
      this.addEventListener('dragstart', (event) => event.preventDefault());

      // Cards depend on stylesheets loaded elsewhere in the page, so a one-time
      // width measurement can race the CSS and read a stale (pre-flex) layout.
      // A ResizeObserver re-fires whenever the group's real size settles.
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.group);
      this.resizeObserver.observe(this);

      if (!this.reducedMotion) {
        this.rafId = requestAnimationFrame(this.tick.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      clearTimeout(this.resumeTimeout);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    handleResize() {
      const width = this.group.getBoundingClientRect().width;
      if (width <= 0 || width === this.groupWidth) return;
      this.groupWidth = width;
      this.offset = this.normalize(this.offset);
      this.fillTrack();
    }

    fillTrack() {
      if (this.groupWidth <= 0) return;
      // Extra groupWidth of margin: a "prev" click/drag can hop the raw offset
      // up by a full loop before it settles, so keep that much slack cloned ahead.
      const targetWidth = this.getBoundingClientRect().width * 2 + this.groupWidth * 2;
      while (this.track.scrollWidth < targetWidth) {
        const clone = this.group.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
      }
    }

    normalize(offset) {
      if (!this.groupWidth) return 0;
      return ((offset % this.groupWidth) + this.groupWidth) % this.groupWidth;
    }

    // Clears the click-nudge transition and folds the offset back to a small
    // equivalent value. The track repeats, so this never changes what's on
    // screen - safe to call any time, a no-op once already settled.
    settle() {
      this.track.classList.remove(NUDGE_CLASS);
      this.offset = this.normalize(this.offset);
    }

    render() {
      this.track.style.transform = `translateX(${-this.offset}px)`;
    }

    tick(time) {
      if (this.lastTime === null) this.lastTime = time;
      const delta = time - this.lastTime;
      this.lastTime = time;

      if (this.groupWidth > 0 && !this.paused && !(this.pauseOnHover && this.hovering)) {
        const pxPerMs = this.groupWidth / (this.speedSeconds * 1000);
        this.offset = this.normalize(this.offset + pxPerMs * delta);
        this.render();
      }

      this.rafId = requestAnimationFrame(this.tick.bind(this));
    }

    getStep() {
      const items = this.group.querySelectorAll('.product-marquee__item');
      if (!items.length) return 0;
      return items.length > 1
        ? items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left
        : items[0].getBoundingClientRect().width;
    }

    nudge(dir) {
      if (!this.groupWidth) return;
      const step = this.getStep();
      if (!step) return;

      // Never let the transition target wrap backwards past 0 (there are no
      // clones to the left of the real group) - hop the current position up by
      // one full loop first, which is visually identical since the track repeats.
      if (dir < 0 && this.offset - step < 0) {
        this.offset += this.groupWidth;
      }

      this.offset += dir * step;
      this.track.classList.add(NUDGE_CLASS);
      this.render();
      this.pauseTemporarily();
    }

    onPointerDown(event) {
      if ((event.pointerType === 'mouse' && event.button !== 0) || !this.groupWidth) return;

      this.dragging = true;
      this.dragAxis = null;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartOffset = this.offset;

      this.paused = true;
      clearTimeout(this.resumeTimeout);
      this.track.classList.remove(NUDGE_CLASS);
      this.classList.add('is-dragging');
    }

    onPointerMove(event) {
      if (!this.dragging) return;

      const dx = event.clientX - this.dragStartX;
      const dy = event.clientY - this.dragStartY;

      if (this.dragAxis === null) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        this.dragAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (this.dragAxis === 'y') {
          // Mostly-vertical gesture: leave it to the page to scroll.
          this.dragging = false;
          this.paused = false;
          this.classList.remove('is-dragging');
          return;
        }
        this.setPointerCapture?.(event.pointerId);
      }

      event.preventDefault();

      let next = this.dragStartOffset - dx;
      // Keep the running value bounded during a long drag (the track is
      // periodic, so folding a whole loop in/out never changes what's on screen).
      while (next < 0) {
        next += this.groupWidth;
        this.dragStartOffset += this.groupWidth;
      }
      while (next >= this.groupWidth * 2) {
        next -= this.groupWidth;
        this.dragStartOffset -= this.groupWidth;
      }

      this.offset = next;
      this.render();
    }

    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.classList.remove('is-dragging');
      this.settle();
      this.pauseTemporarily();
    }

    onWheel(event) {
      if (!this.groupWidth || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      event.preventDefault();
      this.track.classList.remove(NUDGE_CLASS);
      this.offset = this.normalize(this.offset + event.deltaX);
      this.render();
      this.pauseTemporarily();
    }

    pauseTemporarily() {
      this.paused = true;
      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = setTimeout(() => {
        this.paused = false;
        this.settle();
      }, RESUME_DELAY);
    }
  }

  customElements.define('product-marquee', ProductMarquee);
})();
